import React, { useMemo, useState } from "react";
import { Link } from "wouter";
import { useGetMyTeacherCalendar } from "@workspace/api-client-react";
import type { TeacherCalendarSession } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Video,
  Radio,
  ExternalLink,
} from "lucide-react";

type CalendarSession = TeacherCalendarSession;

const TOPIC_ACCENT: Record<
  string,
  { dot: string; chip: string }
> = {
  python: { dot: "bg-primary", chip: "bg-primary/10 text-primary border-primary/20" },
  vibecoding: {
    dot: "bg-secondary",
    chip: "bg-secondary/30 text-foreground border-secondary/40",
  },
  ai: { dot: "bg-accent", chip: "bg-accent/15 text-accent border-accent/30" },
};

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function dayKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function sameDay(a: Date, b: Date): boolean {
  return dayKey(a) === dayKey(b);
}

function isLiveNow(s: CalendarSession): boolean {
  return Boolean(s.zoomLiveStartedAt);
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

export function TeacherCalendar() {
  const { data, isLoading, isError } = useGetMyTeacherCalendar();
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
  const today = new Date();

  const sessions = useMemo<CalendarSession[]>(() => data ?? [], [data]);

  // Group every session by its local calendar day for quick cell lookup.
  const byDay = useMemo(() => {
    const map = new Map<string, CalendarSession[]>();
    for (const s of sessions) {
      const key = dayKey(new Date(s.zoomScheduledAt));
      const arr = map.get(key);
      if (arr) arr.push(s);
      else map.set(key, [s]);
    }
    return map;
  }, [sessions]);

  // Build the 6-week grid covering the visible month.
  const cells = useMemo(() => {
    const first = startOfMonth(cursor);
    const gridStart = new Date(first);
    gridStart.setDate(first.getDate() - first.getDay());
    const out: Date[] = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      out.push(d);
    }
    return out;
  }, [cursor]);

  // Sessions inside the visible month, ordered, for the agenda panel.
  const monthAgenda = useMemo(() => {
    return sessions
      .filter((s) => {
        const d = new Date(s.zoomScheduledAt);
        return (
          d.getFullYear() === cursor.getFullYear() &&
          d.getMonth() === cursor.getMonth()
        );
      })
      .sort(
        (a, b) =>
          new Date(a.zoomScheduledAt).getTime() -
          new Date(b.zoomScheduledAt).getTime(),
      );
  }, [sessions, cursor]);

  const monthLabel = cursor.toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  });

  const goPrev = () =>
    setCursor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1));
  const goNext = () =>
    setCursor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1));
  const goToday = () => setCursor(startOfMonth(new Date()));

  if (isLoading) {
    return (
      <div className="py-16 text-center text-muted-foreground" role="status">
        Loading your schedule…
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-muted/40 rounded-2xl p-10 text-center border border-dashed border-border text-muted-foreground">
        Couldn't load your calendar. Try refreshing the page.
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <div className="bg-muted/40 rounded-2xl p-12 text-center border border-dashed border-border">
        <CalendarDays
          className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-60"
          aria-hidden="true"
        />
        <h3 className="font-display text-2xl font-bold mb-2">
          No sessions scheduled yet
        </h3>
        <p className="text-muted-foreground max-w-sm mx-auto">
          Once you set Zoom dates on your live course sections, every session
          will show up here in one master calendar.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="teacher-calendar">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={goPrev}
            aria-label="Previous month"
            data-testid="calendar-prev"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={goNext}
            aria-label="Next month"
            data-testid="calendar-next"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
          <div className="font-display text-xl md:text-2xl font-bold ml-1">
            {monthLabel}
          </div>
        </div>
        <Button
          variant="ghost"
          onClick={goToday}
          className="font-mono text-[11px] uppercase tracking-[0.2em] self-start sm:self-auto"
          data-testid="calendar-today"
        >
          Today
        </Button>
      </div>

      {/* Month grid */}
      <div className="rounded-2xl border border-border overflow-hidden bg-card">
        <div className="grid grid-cols-7 border-b border-border bg-muted/40">
          {WEEKDAYS.map((w) => (
            <div
              key={w}
              className="px-2 py-2 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground"
            >
              {w}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((d, i) => {
            const inMonth = d.getMonth() === cursor.getMonth();
            const events = byDay.get(dayKey(d)) ?? [];
            const isToday = sameDay(d, today);
            return (
              <div
                key={i}
                className={`min-h-[92px] md:min-h-[104px] border-b border-r border-border p-1.5 last:border-r-0 ${
                  i % 7 === 6 ? "border-r-0" : ""
                } ${inMonth ? "" : "bg-muted/20"}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span
                    className={`inline-flex items-center justify-center text-xs font-medium h-6 w-6 rounded-full ${
                      isToday
                        ? "bg-primary text-primary-foreground"
                        : inMonth
                          ? "text-foreground"
                          : "text-muted-foreground/50"
                    }`}
                  >
                    {d.getDate()}
                  </span>
                  {events.length > 0 && (
                    <span className="font-mono text-[10px] text-muted-foreground">
                      {events.length}
                    </span>
                  )}
                </div>
                <div className="space-y-1">
                  {events.slice(0, 3).map((s) => {
                    const accent =
                      TOPIC_ACCENT[s.courseTopic] ?? TOPIC_ACCENT.python;
                    return (
                      <Link
                        key={`${s.sectionId}-${s.lessonId}`}
                        href={`/teacher/courses/${s.courseSlug}/edit`}
                        className={`block truncate rounded border px-1.5 py-0.5 text-[10px] leading-tight hover:opacity-80 transition-opacity ${accent.chip}`}
                        title={`${formatTime(s.zoomScheduledAt)} · ${s.courseTitle} — ${s.lessonTitle} (${s.sectionLabel})`}
                      >
                        <span className="font-medium">
                          {formatTime(s.zoomScheduledAt)}
                        </span>{" "}
                        {s.courseTitle}
                      </Link>
                    );
                  })}
                  {events.length > 3 && (
                    <div className="text-[10px] text-muted-foreground pl-1">
                      +{events.length - 3} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Agenda for the visible month */}
      <div>
        <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-3">
          // {monthLabel} agenda
        </div>
        {monthAgenda.length === 0 ? (
          <div className="bg-muted/40 rounded-xl p-8 text-center border border-dashed border-border text-muted-foreground text-sm">
            No sessions scheduled this month.
          </div>
        ) : (
          <ul className="space-y-2">
            {monthAgenda.map((s) => {
              const accent = TOPIC_ACCENT[s.courseTopic] ?? TOPIC_ACCENT.python;
              const start = new Date(s.zoomScheduledAt);
              const live = isLiveNow(s);
              return (
                <li
                  key={`${s.sectionId}-${s.lessonId}`}
                  className="flex items-center gap-4 rounded-xl border border-border bg-card p-3 md:p-4"
                  data-testid="agenda-item"
                >
                  <div className="flex flex-col items-center justify-center w-14 shrink-0 text-center">
                    <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                      {start.toLocaleDateString(undefined, { weekday: "short" })}
                    </span>
                    <span className="font-display text-2xl font-bold leading-none">
                      {start.getDate()}
                    </span>
                  </div>
                  <div className="w-px self-stretch bg-border" aria-hidden="true" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className={`inline-block h-2 w-2 rounded-full ${accent.dot}`}
                        aria-hidden="true"
                      />
                      <span className="font-medium truncate">
                        {s.courseTitle}
                      </span>
                      <Badge variant="outline" className="rounded-full text-[10px]">
                        {s.sectionLabel}
                      </Badge>
                      {live && (
                        <Badge className="rounded-full text-[10px] bg-accent text-accent-foreground gap-1">
                          <Radio className="w-3 h-3" /> Live
                        </Badge>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground truncate mt-0.5">
                      Lesson {s.lessonOrderIndex + 1}: {s.lessonTitle}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="font-mono text-xs text-muted-foreground hidden sm:inline">
                      {formatTime(s.zoomScheduledAt)}
                    </span>
                    {s.zoomUrl ? (
                      <a
                        href={s.zoomUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                      >
                        <Video className="w-3.5 h-3.5" /> Zoom
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    ) : (
                      <Link
                        href={`/teacher/courses/${s.courseSlug}/edit`}
                        className="text-xs font-medium text-muted-foreground hover:text-foreground hover:underline"
                      >
                        Add link
                      </Link>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
